import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Flex, Strong, Text } from '@radix-ui/themes';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonProgress } from 'components/common/progress';
import { LandingFormWrapper } from 'components/main/landing/forms/wrapper';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from 'stores/use-auth-store';

const LOADING_MS = 1_000;
const PROGRESS_INTERVAL_MS = 50;

enum Step {
  Loading,
  Login,
  Error,
  Verification,
}

const VERIFICATION_URL_PARAM_KEY = 'verified';
const VERIFICATION_URL_PARAM_VALUE = '1';

export const LoginFormAuth0 = () => {
  const error = useAuthStore((s) => s.error);
  const loading = useAuthStore((s) => s.loading);

  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(Step.Loading);
  const {
    user: userAuth0,
    loginWithRedirect,
    logout: logoutAuth0,
  } = useAuth0();

  const [searchParams] = useSearchParams();
  const verifiedFlag = searchParams.get(VERIFICATION_URL_PARAM_KEY);

  const needsVerification = error?.type === 'verification';

  useEffect(() => {
    const progressInterval = setInterval(() => {
      if (progress === 100 && !loading) {
        clearInterval(progressInterval);

        setTimeout(() => {
          if (!error) {
            setStep(Step.Login);
            return;
          }

          if (needsVerification) {
            setStep(Step.Verification);
            return;
          }

          setStep(Step.Error);
        }, LOADING_MS / 2);
        return;
      }

      const next = Math.min(100, progress + LOADING_MS / PROGRESS_INTERVAL_MS);
      if (next === progress) {
        return;
      }

      setProgress(next);
    }, PROGRESS_INTERVAL_MS);

    return () => clearInterval(progressInterval);
  }, [progress, loading, error, needsVerification]);

  useEffect(() => {
    if (error) {
      console.error({
        event: 'LoginFormAuth0: detected an error',
        error,
      });

      if (error.type === 'verification') {
        setStep(Step.Verification);
      } else {
        setStep(Step.Error);
      }
    }
  }, [error]);

  useEffect(() => {
    // verification email should redirect to login with this query param set
    if (verifiedFlag === VERIFICATION_URL_PARAM_VALUE) {
      // force re-login to refresh auth0 claims to reflect verified email
      loginWithRedirect({
        authorizationParams: {
          redirect_uri: window.origin,
        },
      });
    }
  }, [verifiedFlag]);

  const renderContent = () => {
    if (loading || step === Step.Loading) {
      return <CommonProgress color={RADIX.COLOR.INFO} value={progress} />;
    }

    if (step === Step.Verification) {
      const helpCenterUrl = t('common.intercom-url') + HELP_URLS.LANDING;
      return (
        <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
          <Text>
            <Trans
              i18nKey="common.verify-email-prompt"
              t={t}
              values={{
                x: userAuth0?.email,
              }}
              components={[<Strong key="0" />]}
            />
          </Text>
          <Text>
            <Trans
              i18nKey="common.verify-email-help"
              t={t}
              components={[<a href={helpCenterUrl} key="0" />]}
            />
          </Text>
        </Flex>
      );
    }

    if (step === Step.Error && error) {
      return (
        <>
          <Box>
            <Text align="center">
              {error.message} ({(error.type ?? 'unknown').toUpperCase()}
              ).
            </Text>{' '}
            <Text align="center">Please logout and try again.</Text>
          </Box>
          <Box>
            <Button
              color={RADIX.COLOR.DANGER}
              className="btn-block"
              onClick={() =>
                logoutAuth0({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                })
              }
            >
              Logout
            </Button>
          </Box>
        </>
      );
    }

    if (step === Step.Login) {
      return (
        <CommonFormGrid columns={2}>
          <Box>
            <Button
              color={RADIX.COLOR.SUCCESS}
              className="btn-block"
              onClick={() =>
                loginWithRedirect({
                  authorizationParams: {
                    redirect_uri: window.origin,
                  },
                })
              }
            >
              Login
            </Button>
          </Box>
          <Box>
            <Button
              className="btn-block"
              onClick={() =>
                loginWithRedirect({
                  authorizationParams: {
                    screen_hint: 'signup',
                    redirect_uri: window.origin,
                  },
                })
              }
            >
              Sign Up
            </Button>
          </Box>
        </CommonFormGrid>
      );
    }
  };

  const getHeader = () => {
    if (loading) {
      return 'Intializing Auth0 client...';
    }

    if (needsVerification) {
      return 'Email Verification Required';
    }

    if (error) {
      return 'There was a problem with your request.';
    }

    return 'Please use Auth0 to authenticate your session.';
  };

  return (
    <ErrorBoundary identifier="LoginFormAuth0" hideIntercom>
      <LandingFormWrapper header={getHeader()}>
        <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
          {renderContent()}
        </Flex>
      </LandingFormWrapper>
    </ErrorBoundary>
  );
};
